import {
  AddNotificationCountToIndexedDB,
  AddReadNotificationToIndexedDB,
  AddUnreadNotificationToIndexedDB,
  DeleteNotificationIndexedDB,
  GetNotificationCountFromIndexedDB,
  GetReadNotificationFromIndexedDB,
  GetUnreadNotificationFromIndexedDB,
  UpdateNotificationCountToIndexedDB,
  UpdateReadNotificationToIndexedDB,
  UpdateUnreadNotificationToIndexedDB,
} from "@/IndexedDB/notificationIndexedDB";
import { CheckIndexedDBIsSupport, DeleteTabFromIndexedDB } from "@/IndexedDB/tabIndexedDB";
import { CheckBroadcastIsSupport } from "@/broadcastChannel/tabChannel";
import {
  BroadcastDisconnectNotificationAllTab,
  BroadcastIsUpdateNotificationCount,
  BroadcastIsUpdateNotificationList,
  BroadcastMasterDeleteNotificationById,
  BroadcastMasterSetNotificationIsSeenAll,
  BroadcastReCreateTab,
} from "@/broadcastChannel/userChannel";
import { NotificationMessageType } from "@/enums/enums";
import { i18n } from "@/i18n";
import { NotificationMessageModel } from "@/model/notification/NotificationMessageModel";
import { NotificationModel } from "@/model/notification/NotificationModel";
import store from "@/store";
import Vue from "vue";
import {
  ClearTabFromLocalStorage,
  ClearTabFromSessionStorage,
  GetAllTabs,
  GetTabIdFromSessionStorage,
  InsertTabIdToTabDB,
  IsMasterTab,
  IsMasterTabAvailable,
  SetMasterTab,
  UpdateTabMerchantId,
} from "./tabHelper";
import { AccessToken, UserId } from "./userHelper";

const appVue = new Vue();

let timerCheckAccessToken = 0;

export const SetTimerCheckAccessToken = () => {
  if (IsMasterTab()) {
    timerCheckAccessToken = setInterval(async () => {
      const isCanUseAccessToken = await store.dispatch("LogIn/DoIntrospectAccessToken", null, { root: true });
      if (isCanUseAccessToken) {
        await store.dispatch("LogIn/DoUpdateAccessToken", null, { root: true });
      } else {
        const isCanNewUseAccessToken = await store.dispatch("LogIn/DoGetNewAccessTokenByRefreshToken", null, {
          root: true,
        });
        if (isCanNewUseAccessToken) {
          await store.dispatch("LogIn/DoUpdateAccessToken", null, { root: true });
        }
      }
    }, 3660000);
  }
};

export const ClearTimerCheckAccessToken = () => {
  if (IsMasterTab()) {
    clearInterval(timerCheckAccessToken);
  }
};

export const CheckStatusSocket = () => {
  if (IsMasterTab()) {
    if (!appVue.$socket || appVue.$socket.disconnected) {
      appVue.$socket.connect();
    }
  } else if (appVue.$socket && appVue.$socket.connected) {
    appVue.$socket.disconnect();
  }
};

export const SocketConnect = () => {
  if (IsMasterTab()) {
    if (appVue.$socket && appVue.$socket.disconnected) {
      appVue.$socket.connect();
    }
  } else if (!IsMasterTabAvailable()) {
    SetMasterTab();
    if (appVue.$socket && appVue.$socket.disconnected) {
      appVue.$socket.connect();
    }
    store.commit("Notification/MutationSetNotificationReadyToUse", true);
  } else {
    store.commit("Notification/MutationSetNotificationReadyToUse", true, { root: true });
  }
};

export const SocketAuthentication = () => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    const eui = btoa(UserId());
    const accessToken = AccessToken();
    appVue.$socket.emit("authentication", {
      eui: eui,
      accessToken: accessToken,
    });
  }
};

export const SocketDisconnect = () => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    appVue.$socket.disconnect();
  }
};

export const DoGetAllNotificationList = (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch("Notification/DoGetAllNotificationList", merchantId, { root: true });
  }
};

export const DoGetUnseenNotificationCount = (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch("Notification/DoGetUnseenNotificationCount", merchantId, { root: true });
  }
};

export const DoDeleteSeenNotification = (merchantId: number, notificationId: string) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch(
      "Notification/DoDeleteSeenNotification",
      { merchantId: merchantId, notificationId: notificationId },
      { root: true }
    );
  }
};

export const DoDeleteUnseenNotification = (merchantId: number, notificationId: string) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch(
      "Notification/DoDeleteUnseenNotification",
      { merchantId: merchantId, notificationId: notificationId },
      { root: true }
    );
  }
};

export const DoSetAllNotificationIsSeen = (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch("Notification/DoSetAllNotificationIsSeen", merchantId, { root: true });
  }
};

export const DoAddNotificationCount = async (merchantId: number, notificationCount: number) => {
  const notificationCountModelForInsert = {
    merchantId: merchantId,
    notificationCount: notificationCount,
  };
  const notificationCountModel = await GetNotificationCountFromIndexedDB(merchantId);
  if (notificationCountModel) {
    await UpdateNotificationCountToIndexedDB(notificationCountModelForInsert);
  } else {
    await AddNotificationCountToIndexedDB(notificationCountModelForInsert);
  }
};

export const DoGetNotificationCount = async (merchantId: number) => {
  const notificationCountModel = await GetNotificationCountFromIndexedDB(merchantId);
  return notificationCountModel ? notificationCountModel.notificationCount : 0;
};

export const DoAddUnreadNotificationList = async (
  merchantId: number,
  notificationList: Array<NotificationMessageModel>
) => {
  const notificationModelForInsert: NotificationModel = {
    merchantId: merchantId,
    notificationList: notificationList,
  };

  const notificationModel = await GetUnreadNotificationFromIndexedDB(merchantId);

  if (notificationModel) {
    await UpdateUnreadNotificationToIndexedDB(notificationModelForInsert);
  } else {
    await AddUnreadNotificationToIndexedDB(notificationModelForInsert);
  }
};

export const DoGetReadAllNotificationList = async (merchantId: number) => {
  const notificationModel = await GetReadNotificationFromIndexedDB(merchantId);
  return notificationModel ? notificationModel.notificationList : [];
};

export const DoAddReadNotificationList = async (
  merchantId: number,
  notificationList: Array<NotificationMessageModel>
) => {
  const notificationModelForInsert = {
    merchantId: merchantId,
    notificationList: notificationList,
  };
  const notificationModel = await GetReadNotificationFromIndexedDB(merchantId);
  if (notificationModel) {
    await UpdateReadNotificationToIndexedDB(notificationModelForInsert);
  } else {
    await AddReadNotificationToIndexedDB(notificationModelForInsert);
  }
};

export const DoGetUnreadAllNotificationList = async (merchantId: number) => {
  const notificationModel = await GetUnreadNotificationFromIndexedDB(merchantId);
  return notificationModel ? notificationModel.notificationList : [];
};

export const RemoveNotificationFromNotificationList = (
  notificationList: Array<NotificationMessageModel>,
  notificationId: string
) => {
  if (notificationList && notificationList.length > 0) {
    const findIndex = notificationList.findIndex((x) => x.id === notificationId);
    if (findIndex > -1) {
      notificationList.splice(findIndex, 1);
      return notificationList;
    }
    return notificationList;
  }
  return [];
};

export const DoDeleteNotificationById = async (merchantId: number, notificationId: string, isSeen: boolean) => {
  if (IsMasterTab()) {
    if (isSeen) {
      DoDeleteSeenNotification(merchantId, notificationId);
      let notificationList = await DoGetReadAllNotificationList(merchantId);
      notificationList = RemoveNotificationFromNotificationList(notificationList, notificationId);
      await UpdateReadNotificationToIndexedDB({ merchantId: merchantId, notificationList: notificationList });
    } else {
      DoDeleteUnseenNotification(merchantId, notificationId);
      let notificationList = await DoGetUnreadAllNotificationList(merchantId);
      notificationList = RemoveNotificationFromNotificationList(notificationList, notificationId);
      await UpdateUnreadNotificationToIndexedDB({ merchantId: merchantId, notificationList: notificationList });
      const notificationCount = await DoGetNotificationCount(merchantId);
      await UpdateNotificationCountToIndexedDB({ merchantId: merchantId, notificationCount: notificationCount - 1 });
      await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
      BroadcastIsUpdateNotificationCount(merchantId);
    }
    await store.dispatch("Notification/ActionSetNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationList(merchantId);
  } else {
    BroadcastMasterDeleteNotificationById(merchantId, notificationId, isSeen);
  }
};

export const DoDeleteNotificationByIdFromEmit = async (merchantId: number, notificationId: string) => {
  if (IsMasterTab()) {
    let readNotificationList = await DoGetReadAllNotificationList(merchantId);
    readNotificationList = RemoveNotificationFromNotificationList(readNotificationList, notificationId);
    await UpdateReadNotificationToIndexedDB({ merchantId: merchantId, notificationList: readNotificationList });

    let unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    unreadNotificationList = RemoveNotificationFromNotificationList(unreadNotificationList, notificationId);
    await UpdateUnreadNotificationToIndexedDB({ merchantId: merchantId, notificationList: unreadNotificationList });
    const notificationCount = await DoGetNotificationCount(merchantId);
    await UpdateNotificationCountToIndexedDB({ merchantId: merchantId, notificationCount: notificationCount - 1 });
    await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationCount(merchantId);

    await store.dispatch("Notification/ActionSetNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationList(merchantId);
  }
};

export const DoSetNotificationIsSeenAll = async (merchantId: number) => {
  if (IsMasterTab()) {
    DoSetAllNotificationIsSeen(merchantId);
    const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    const readNotificationList = await DoGetReadAllNotificationList(merchantId);
    await UpdateUnreadNotificationToIndexedDB({ merchantId: merchantId, notificationList: [] });
    await UpdateNotificationCountToIndexedDB({ merchantId: merchantId, notificationCount: 0 });

    await DoAddReadNotificationList(merchantId, [...unreadNotificationList, ...readNotificationList]);

    await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationCount(merchantId);

    await store.dispatch("Notification/ActionSetNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationList(merchantId);
  } else {
    BroadcastMasterSetNotificationIsSeenAll(merchantId);
  }
};

export const SetIsSeenForNotification = (notificationList: Array<NotificationMessageModel>, isSeen: boolean) => {
  if (notificationList && notificationList.length > 0) {
    notificationList.forEach((notificationItem) => {
      notificationItem.isSeen = isSeen;
    });
    return notificationList;
  }
  return [];
};

export const SortDataNotification = (notificationList: Array<NotificationMessageModel>) => {
  if (notificationList && notificationList.length > 0) {
    return notificationList.sort((a, b) => {
      const notificationDateTimeA = new Date(a.notificationDateTime);
      const notificationDateTimeB = new Date(b.notificationDateTime);
      return notificationDateTimeB.getTime() - notificationDateTimeA.getTime();
    });
  }
  return [];
};

export const CheckMerchantIsActiveOtherTab = async (merchantId: number) => {
  const tabId = GetTabIdFromSessionStorage();
  if (tabId) {
    const allTabs = await GetAllTabs();
    return allTabs.some((x) => x.id !== tabId && x.merchantId == merchantId);
  }
  return false;
};

export const CheckMerchantIsLoadNotification = async (merchantId: number) => {
  const unreadNotificationModel = await GetUnreadNotificationFromIndexedDB(merchantId);
  const readNotificationModel = await GetReadNotificationFromIndexedDB(merchantId);
  return unreadNotificationModel || readNotificationModel ? true : false;
};

export const CheckMerchantIsActive = async (merchantId: number) => {
  let isActive = false;
  if (merchantId > 0) {
    if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
      const allTabs = await GetAllTabs();
      const isHaveSomeTabSameMerchant = allTabs.some((x) => x.merchantId == merchantId);
      if (isHaveSomeTabSameMerchant) {
        isActive = true;
      }
    } else {
      isActive = true;
    }
  }
  return isActive;
};

export const ForceSetMaster = async (merchantId: number) => {
  BroadcastDisconnectNotificationAllTab();
  ClearTabFromLocalStorage();
  ClearTabFromSessionStorage();
  await DeleteTabFromIndexedDB();
  await DeleteNotificationIndexedDB();
  await InsertTabIdToTabDB();
  await UpdateTabMerchantId(merchantId);
  DoGetUnseenNotificationCount(merchantId);
  BroadcastReCreateTab();
};

export const GetSuccessHeaderReport = (messageTypeId: NotificationMessageType, messageDescription: string) => {
  let headerMessage: any = "";
  const _messageDescription = messageDescription ? messageDescription : "";
  switch (messageTypeId) {
    case NotificationMessageType.Quotation:
      headerMessage = i18n.t("Success_QuotationReport");
      break;
    case NotificationMessageType.PurcahseOrder:
      headerMessage = i18n.t("Success_POReport");
      break;
    case NotificationMessageType.Invoice:
    case NotificationMessageType.InvoiceAllProduct:
      headerMessage = i18n.t("Success_InvoiceReport");
      break;
    case NotificationMessageType.Expense:
    case NotificationMessageType.ExpenseAllProduct:
      headerMessage = i18n.t("Success_ExpenseReport");
      break;
    case NotificationMessageType.PurchaseAsset:
    case NotificationMessageType.PurchaseAssetAllProduct:
      headerMessage = i18n.t("Success_PurchaseAssetReport");
      break;
    case NotificationMessageType.Receipt:
      headerMessage = i18n.t("Success_ReceiptReport");
      break;
    case NotificationMessageType.TaxInvoice:
    case NotificationMessageType.TaxInvoiceInRevenueDepartmentFormat:
      headerMessage = i18n.t("Success_TaxInvoiceReport");
      break;
    case NotificationMessageType.PurcahseTaxInvoice:
    case NotificationMessageType.PurchaseTaxInvoiceInRevenueDepartFormat:
      headerMessage = i18n.t("Success_PurchaseTaxInvoiceReport");
      break;
    case NotificationMessageType.AllReportTaxInvoiceFromOldPeak:
      headerMessage = i18n.t("Success_TaxInvoiceReportFromOldPeak");
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceFromOldPeak:
      headerMessage = i18n.t("Success_PurchaseTaxInvoiceReportFromOldPeak");
      break;
    case NotificationMessageType.AllReportTaxInvoiceFromPeakTax:
      headerMessage = i18n.t("Success_TaxInvoiceReportFromPeakTax");
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceFromPeakTax:
      headerMessage = i18n.t("Success_PurchaseTaxInvoiceReportFromPeakTax");
      break;
    case NotificationMessageType.BillingNoteInvoice:
    case NotificationMessageType.BillingNoteInvoiceAllInvoice:
      headerMessage = i18n.t("Success_BillingNoteReport");
      break;
    case NotificationMessageType.BillingNoteExpense:
    case NotificationMessageType.BillingNoteExpenseAllExpense:
      headerMessage = i18n.t("Success_CombinePaymentNoteReport");
      break;
    case NotificationMessageType.StockActivityStockCardMovement:
      headerMessage = i18n.t("Success_StockMovementReport");
      break;
    case NotificationMessageType.Journal:
      headerMessage = i18n.t("Success_DailyJournalReport");
      break;
    case NotificationMessageType.TrialBalance:
    case NotificationMessageType.TrialBalanceWithSubAccount:
      headerMessage = i18n.t("Success_TrialBalanceReport");
      break;
    case NotificationMessageType.TrialBalanceSeperateMonth:
      headerMessage = i18n.t("Success_TrialBalanceSeperateMonthReport");
      break;
    case NotificationMessageType.Finance:
      headerMessage = i18n.t("Success_FinanceReport");
      break;
    case NotificationMessageType.PP30InputVat:
      headerMessage = i18n.t("Success_PP30InputVat");
      break;
    case NotificationMessageType.PP30OutputVat:
      headerMessage = i18n.t("Success_PP30OutputVat");
      break;
    case NotificationMessageType.CreditNote:
      headerMessage = i18n.t("Success_CreditnoteReport");
      break;
    case NotificationMessageType.CreditNoteExpense:
      headerMessage = i18n.t("Success_CreditnoteExpenseReport");
      break;
    case NotificationMessageType.AssetTemplate:
      headerMessage = i18n.t("Success_AssetTemplateReport");
      break;
    case NotificationMessageType.AssetTemplateGroup:
      headerMessage = i18n.t("Success_AssetTemplateGroupReport");
      break;
    case NotificationMessageType.WithHeldTax:
      headerMessage = i18n.t("Success_WithHeldTaxReport");
      break;
    case NotificationMessageType.WithHoldingTax:
      headerMessage = i18n.t("Success_WithHoldingTaxReport");
      break;
    case NotificationMessageType.GeneralLedgerTransaction:
      headerMessage = i18n.t("Success_GeneralLedger_Transaction");
      break;
    case NotificationMessageType.AllGeneralLedger:
      headerMessage = i18n.t("Success_AllGeneralLedger");
      break;
    case NotificationMessageType.AllGeneralLedgerWithSubAccount:
      headerMessage = i18n.t("Success_GeneralLedger_Transaction");
      break;
    case NotificationMessageType.AgingReportAR:
      headerMessage = i18n.t("Success_Aging_Report_AR");
      break;
    case NotificationMessageType.AgingReportAP:
      headerMessage = i18n.t("Success_Aging_Report_AP");
      break;
    case NotificationMessageType.Product:
      headerMessage = i18n.t("Success_Product");
      break;
    case NotificationMessageType.ProfitLoss:
      headerMessage = i18n.t("Success_ProfitLoss");
      break;
    case NotificationMessageType.ChartOfAccount:
      headerMessage = i18n.t("Success_ChartOfAccount");
      break;
    case NotificationMessageType.BalanceSheet:
      headerMessage = i18n.t("Success_BalanceSheet");
      break;
    case NotificationMessageType.Unit:
      headerMessage = i18n.t("Success_Unit");
      break;
    case NotificationMessageType.ReportDocument:
      headerMessage = i18n.t("Success_ReportDocument");
      break;
    case NotificationMessageType.Asset:
      headerMessage = i18n.t("Success_AssetReport");
      break;
    case NotificationMessageType.StatementEFiling:
      headerMessage = i18n.t("Success_StatementEFiling");
      break;
    case NotificationMessageType.Contact:
      headerMessage = i18n.t("Success_ReportContact");
      break;
    case NotificationMessageType.PurchaseTaxInvoiceNotUse:
      headerMessage = i18n.t("AllReportPurchaseTaxInvoiceNotUse");
      break;
    case NotificationMessageType.SalesTaxInvoiceNotUse:
      headerMessage = i18n.t("AllReportSalesTaxInvoiceNotUse");
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceNotUseFromOldPeak:
      headerMessage = i18n.t("AllReportPurchaseTaxInvoiceNotUseFromOldPeak");
      break;
    case NotificationMessageType.AllReportSalesTaxInvoiceNotUseFromOldPeak:
      headerMessage = i18n.t("AllReportSalesTaxInvoiceNotUseFromOldPeak");
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceNotUseFromPeakTax:
      headerMessage = i18n.t("AllReportPurchaseTaxInvoiceNotUseFromPeakTax");
      break;
    case NotificationMessageType.AllReportSalesTaxInvoiceNotUseFromPeakTax:
      headerMessage = i18n.t("AllReportSalesTaxInvoiceNotUseFromPeakTax");
      break;
    case NotificationMessageType.PP30TaxFilingTextFile:
      headerMessage = i18n.t("Success_PP30TaxFilingTextFile");
      break;
    case NotificationMessageType.WithHoldingTaxPND2:
      headerMessage = i18n.t("Success_WithHoldingTaxPND2");
      break;
    case NotificationMessageType.WithHoldingTaxPND3:
      headerMessage = i18n.t("Success_WithHoldingTaxPND3");
      break;
    case NotificationMessageType.WithHoldingTaxPND53:
      headerMessage = i18n.t("Success_WithHoldingTaxPND53");
      break;
    case NotificationMessageType.CashflowStatementReport:
      headerMessage = i18n.t("Success_CashflowStatementReport");
      break;
    case NotificationMessageType.AllReportSalesByCustomerReport:
      headerMessage = i18n.t("Success_AllReportSalesByCustomerReport");
      break;
    case NotificationMessageType.AllReportSalesByAccountReport:
      headerMessage = i18n.t("Success_AllReportSalesByAccountReport");
      break;
    case NotificationMessageType.StatementEFilingExcel:
      headerMessage = i18n.t("Success_StatementEFilingExcel");
      break;
    case NotificationMessageType.AllReportProductSalesByContact:
      headerMessage = i18n.t("Success_AllReportProductSalesByContact");
      break;
    case NotificationMessageType.AllReport_ProductServiceSalesSummaryReportOnDailyAccount:
      headerMessage = i18n.t("Success_AllReport_ProductServiceSalesSummaryReportOnDailyAccount");
      break;
    case NotificationMessageType.AllReport_RemainingFinanceBalanceReport:
      headerMessage = i18n.t("Success_AllReport_RemainingFinanceBalanceReport");
      break;
    case NotificationMessageType.Pnd53TaxFilingTextFile:
      headerMessage = i18n.t("Success_Pnd53TaxFilingTextFile");
      break;
    case NotificationMessageType.Pnd3TaxFilingTextFile:
      headerMessage = i18n.t("Success_Pnd3TaxFilingTextFile");
      break;
    case NotificationMessageType.DailyJournal:
      headerMessage = i18n.t("Success_DailyJournal");
      break;
    case NotificationMessageType.DocumentQuotation:
      headerMessage = i18n.t("Success_DocumentQuotation");
      break;
    case NotificationMessageType.DocumentInvoice:
      headerMessage = i18n.t("Success_DocumentInvoice");
      break;
    case NotificationMessageType.DocumentReceipt:
      headerMessage = i18n.t("Success_DocumentReceipt");
      break;
    case NotificationMessageType.DocumentTaxInvoice:
      headerMessage = i18n.t("Success_DocumentTaxInvoice");
      break;
    case NotificationMessageType.DocumentPurchaseOrder:
      headerMessage = i18n.t("Success_DocumentPurchaseOrder");
      break;
    case NotificationMessageType.DocumentExpense:
      headerMessage = i18n.t("Success_DocumentExpense");
      break;
    case NotificationMessageType.DocumentBillingNote:
      headerMessage = i18n.t("Success_DocumentBillingNote");
      break;
    case NotificationMessageType.DocumentBillingNoteExpense:
      headerMessage = i18n.t("Success_DocumentBillingNoteExpress");
      break;
    case NotificationMessageType.DocumentCreditNote:
      headerMessage = i18n.t("Success_DocumentCreditNote");
      break;
    case NotificationMessageType.DocumentCreditNoteExpense:
      headerMessage = i18n.t("Success_DocumentCreditNoteExpress");
      break;
    case NotificationMessageType.DocumentPurchaseTaxInvoice:
      headerMessage = i18n.t("Success_DocumentPurchaseTaxInvoice");
      break;
    case NotificationMessageType.DocumentWithholdingTax:
      headerMessage = i18n.t("Success_DocumentWithholdingTax");
      break;
    case NotificationMessageType.ImportTransactionPurchaseInventory:
      headerMessage = i18n.t("Success_ImportTransactionPurchaseInventory");
      break;
    case NotificationMessageType.ImportTransactionExpense:
      headerMessage = i18n.t("Success_ImportTransactionExpense");
      break;
    case NotificationMessageType.ImportTransactionInvoice:
      headerMessage = i18n.t("Success_ImportTransactionInvoice");
      break;
    case NotificationMessageType.ImportTransactionReceipt:
      headerMessage = i18n.t("Success_ImportTransactionReceipt");
      break;
    case NotificationMessageType.ImportTransactionQuotation:
      headerMessage = i18n.t("Success_ImportTransactionQuotation");
      break;
    case NotificationMessageType.ImportTransactionPurchaseOrder:
      headerMessage = i18n.t("Success_ImportTransactionPurchaseOrder");
      break;
    case NotificationMessageType.Statement:
      headerMessage = i18n.t("Success_Statement");
      break;
    case NotificationMessageType.AllReportWithHoldingTaxPND2:
      headerMessage = i18n.t("Success_AllReportWithHoldingTaxPND2");
      break;
    case NotificationMessageType.AllReportWithHoldingTaxPND3:
      headerMessage = i18n.t("Success_AllReportWithHoldingTaxPND3");
      break;
    case NotificationMessageType.AllReportWithHoldingTaxPND53:
      headerMessage = i18n.t("Success_AllReportWithHoldingTaxPND53");
      break;
    case NotificationMessageType.ChequeReport:
      headerMessage = i18n.t("Success_ChequeReport");
      break;
    case NotificationMessageType.WorkingPaper:
      headerMessage = i18n.t("Success_WorkingPaperReport");
      break;
    case NotificationMessageType.RecalCostOfGoodSoldByMerchantCalculated:
      headerMessage = i18n.t("Success_RecalCostOfGoodSoldByMerchantCalculated");
      break;
    case NotificationMessageType.RecalCostOfGoodSoldByProductCalculated:
      headerMessage = i18n.t("Success_RecalCostOfGoodSoldByProductCalculated");
      break;
    case NotificationMessageType.RecalCostOfGoodSoldFailed:
      headerMessage = i18n.t("Success_RecalCostOfGoodSoldFailed");
      break;
    case NotificationMessageType.DebitNote:
      headerMessage = i18n.t("Success_DebitNoteReport");
      break;
    case NotificationMessageType.DocumentDebitNote:
      headerMessage = i18n.t("Success_DocumentDebitNote");
      break;
    case NotificationMessageType.DocumentDebitNoteExpense:
      headerMessage = i18n.t("Success_DocumentDebitNoteExpense");
      break;
    case NotificationMessageType.VoidImportTransactionFailed:
      headerMessage = i18n.t("Success_VoidImportTransactionFailed");
      break;
    case NotificationMessageType.VoidImportTransactionSuccess:
      headerMessage = i18n.t("Success_VoidImportTransactionSuccess");
      break;
    case NotificationMessageType.ImportTransactionReceiptFromInvoice:
      headerMessage = i18n.t("Success_ImportTransactionReceiptFromInvoice");
      break;
    case NotificationMessageType.ImportTransactionJournal:
      headerMessage = i18n.t("Success_ImportTransactionJournal");
      break;
    case NotificationMessageType.DepreciateAssetMonthly:
      headerMessage = i18n.t("Success_DepreciateAssetMonthly") + " " + _messageDescription;
      break;
    case NotificationMessageType.TaxPnd:
      headerMessage = i18n.t("Success_WithHoldingTaxReport");
      break;
    case NotificationMessageType.TaxPP30SaleTax:
      headerMessage = i18n.t("Success_TaxInvoiceReport");
      break;
    case NotificationMessageType.TaxPP30PurchaseTax:
      headerMessage = i18n.t("Success_PurchaseTaxInvoiceReport");
      break;
    case NotificationMessageType.TaxFetchPP30Transaction:
      headerMessage = i18n.t("Success_TaxFetchPP30Transaction");
      break;
    case NotificationMessageType.TaxFetchWhtCertificate:
      headerMessage = i18n.t("Success_TaxFetchWhtCertificate");
      break;
    case NotificationMessageType.FinanceBankStatement:
      headerMessage = i18n.t("Success_FinanceBankStatementReport");
      break;
    case NotificationMessageType.ExecutiveSummary:
      headerMessage = i18n.t("Success_ExecutiveSummaryReport");
      break;
    case NotificationMessageType.StatementOfCashflow:
      headerMessage = i18n.t("Success_StatementOfCashflow");
      break;
    case NotificationMessageType.RecieveBenefit:
    case NotificationMessageType.CamPaignBeforeExpire:
    case NotificationMessageType.BeforeExpireByDate:
    case NotificationMessageType.QuotaExpire:
      headerMessage = _messageDescription;
      break;
  }
  return headerMessage;
};
